import { MouseEvent, useCallback } from 'react';

import { ApiSlideshowData } from 'api/types/ApiSlideshowData';
import { Carousel, useCarouselState } from 'components/Carousel';
import { FilestackImage } from 'components/FilestackImage/FilestackImage';
import { VideoThumbnail } from 'components/VideoThumbnail';
import { useIsClient } from 'hooks/useIsClient';

import { SlideshowItem } from './Slideshow.styled';
import { SlideshowAccessibleButton } from './SlideshowAccessibleButton';
import { SlideshowAmpButtonWrapper } from './SlideshowAmpButtonWrapper';
import { SlideshowNavigation } from './SlideshowNavigation';

type Props = {
  carouselId: string;
  items: ApiSlideshowData[];
  onClickIndex?: (data: { itemIndex: number }) => void;
  fullWidth?: boolean;
  round?: boolean;
  'data-qa-id'?: string;
};

export function Slideshow({
  carouselId,
  items,
  onClickIndex,
  fullWidth,
  round,
  'data-qa-id': qaId = 'carousel',
}: Props) {
  const carouselState = useCarouselState({
    slideCount: items.length,
    responsiveSlidesPerPage: {
      xs: 1,
      sm: 1,
      md: 1,
      lg: 3,
    },
    capSlidesPerPageToSlideCount: true,
  });

  const clickAccessibleButton = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      onClickIndex?.({ itemIndex: 0 });
    },
    [onClickIndex],
  );

  const isClient = useIsClient();

  return (
    <div style={{ position: 'relative' }}>
      {onClickIndex && (
        <SlideshowAccessibleButton
          aria-label={getText('Open gallery')}
          title="Open image gallery"
          $navShown={carouselState.pageCount > 1}
          onClick={clickAccessibleButton}
        />
      )}

      <Carousel
        id={carouselId}
        state={carouselState}
        data-qa-id={qaId}
        gutter={round ? 40 : undefined}
        height={360}
        navigation={
          isClient && (
            <SlideshowNavigation
              carouselState={carouselState}
              fullWidth={Boolean(fullWidth)}
            />
          )
        }
        slides={items?.map((item, index) => (
          <SlideshowAmpButtonWrapper carouselId={carouselId}>
            <SlideshowItem
              key={item.handle || item.videoUrl}
              $clickable={Boolean(onClickIndex)}
              data-qa-id={`${qaId}-image-container`}
              aria-label={getText('Open gallery')}
              tabIndex={-1}
              onClick={(e: MouseEvent) => {
                e.preventDefault();
                onClickIndex?.({ itemIndex: index });
              }}
            >
              {item.fileId && (
                <FilestackImage
                  data-qa-id={`${qaId}-image`}
                  data={item}
                  key={item.handle}
                  alt={item.altText || ''}
                  width={fullWidth ? 720 : 360}
                  height={360}
                  objectFit="cover"
                  expandSize
                />
              )}

              {item.type === 'VIDEO' && item.videoUrl && (
                <VideoThumbnail
                  src={{ type: 'video-thumbnail', videoUrl: item.videoUrl }}
                  qaId={`${qaId}-image`}
                  size={{ type: 'expand' }}
                  playButton="large"
                />
              )}
            </SlideshowItem>
          </SlideshowAmpButtonWrapper>
        ))}
      />
    </div>
  );
}
